import { MODULE_STATE } from "@/permissions/modules";
import useModuleAccess from "@/hooks/useModuleAccess";
import { cn } from "@/lib/utils";

/**
 * Render a feature surface only when the tenant may see it.
 *
 * | State         | What renders                                             |
 * |---------------|----------------------------------------------------------|
 * | Not entitled  | **nothing at all** — `null`                              |
 * | Unavailable   | depends on `variant` — see below                         |
 * | Available     | the children, untouched                                  |
 *
 * ## The null branch is the point
 *
 * React does not run a child's effects when the child is not rendered. That
 * is what stops the fetch. A component that mounts and returns early inside
 * itself has already run its `useEffect`, already called the endpoint, and
 * already shown the tenant an error about a feature they never bought.
 *
 * So: never implement the not-entitled branch as `visibility: hidden`,
 * `display: none`, or an early return inside the child. The child must not
 * mount.
 *
 * ## The unavailable branch keeps the data, and says so once
 *
 * A kill switch stops new work, never reads. Whatever the tenant generated
 * before the kill stays on screen. **How** to say that depends entirely on the
 * shape of the child, which is why there are two variants and no default prose:
 *
 * | variant              | unavailable renders                                   |
 * |----------------------|-------------------------------------------------------|
 * | `control` (default)  | the child, `inert`, reason in a native tooltip         |
 * | `passthrough`        | the child, untouched — it explains its own state       |
 *
 * This component used to append the whole "temporarily unavailable" sentence as
 * a paragraph and set `pointer-events-none` on the child, whatever the child
 * was. Both halves were wrong:
 *
 * - the paragraph landed under a small button on every job card and inside a
 *   192px dropdown menu, and it broke both layouts;
 * - `pointer-events-none` over a *panel* made the data unreadable — the Boolean
 *   Strings accordion could not be opened and Copy could not be clicked, which
 *   is the opposite of what the kill switch promises. That panel also has its
 *   own banner, so the sentence appeared twice.
 *
 * A page that wants the sentence places `<ModuleUnavailableNotice>` itself, once,
 * where it reads properly.
 *
 * @param {string}  module     a value from MODULES
 * @param {node}    children   the surface to gate
 * @param {"control"|"passthrough"} [variant]
 * @param {string}  [className] extra classes for the disabled wrapper — pass
 *                              `w-full` when the child fills its row
 */
export const ModuleGate = ({ module, children, variant = "control", className = "" }) => {
  const { state, message } = useModuleAccess(module);

  if (state === MODULE_STATE.NOT_ENTITLED) return null;
  if (state !== MODULE_STATE.UNAVAILABLE) return children;
  if (variant === "passthrough") return children;

  // `title` sits on the outer span, which stays live so a hover still reaches
  // it and shows the reason.
  //
  // `inert` on the inner span is what disables. `pointer-events-none` stops the
  // mouse and nothing else: the child stayed a real `<button>`, kept its place
  // in the tab order, and Enter or Space still fired its `onClick` — so a
  // keyboard user reached Bulk Upload on a killed module and got a 503 from a
  // control the design promises is disabled. `aria-disabled` could not help;
  // it changes what a screen reader announces, never what the browser does.
  // `inert` takes the whole subtree out of the tab order and out of the
  // accessibility tree, whatever shape the child is — a button, a toggle row or
  // a clickable card.
  //
  // `pointer-events-none` stays on the same span so the hover lands on the
  // wrapper and the tooltip keeps working.
  //
  // `inert` also hides the child's label from a screen reader, so the reason is
  // repeated as visually-hidden text. Without it the control would simply
  // vanish for anyone who is not looking at the screen.
  return (
    <span title={message} aria-disabled="true" className={cn("inline-flex max-w-full cursor-not-allowed opacity-50", className)}>
      <span inert={true} className="pointer-events-none inline-flex w-full">
        {children}
      </span>
      <span className="sr-only">{message}</span>
    </span>
  );
};

export default ModuleGate;
